import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useStore } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { initAuthMode } from '../lib/authMode'
import { clearLocalStoragePreservingDeviceId } from '../lib/deviceId'
const PETALS = Array.from({ length: 8 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 90 + 5}%`,
  size: Math.random() * 12 + 8,
  duration: Math.random() * 7 + 8,
  delay: Math.random() * 6,
  opacity: Math.random() * 0.4 + 0.4,
}))

export default function Splash() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setBalance, restoreSession } = useStore()
  const { t } = useI18n()

  useEffect(() => {
    const timer = setTimeout(async () => {
      // Initialize auth mode from URL params (?web3=true)
      initAuthMode(searchParams)

      const skipOnboarding = searchParams.get('skip_onboarding') === 'true'
      const balanceParam = searchParams.get('balance')
      const modeParam = searchParams.get('mode')

      // ?mode=store → store dashboard (with optional ?store=store_001)
      if (modeParam === 'store') {
        const storeParam = searchParams.get('store') || 'store_001'
        navigate(`/store/dashboard/${storeParam}`, { replace: true })
        return
      }

      // ?balance=50000 → restore session, then override balance (demo mode)
      if (balanceParam) {
        const amount = parseInt(balanceParam, 10)
        if (!isNaN(amount) && amount > 0) {
          await restoreSession()
          setBalance(amount)
          navigate('/home', { replace: true })
          return
        }
      }

      // Check localStorage for returning user → restore from Firestore
      const isLoggedIn = localStorage.getItem('isLoggedIn')
      if (isLoggedIn) {
        // Session expiry check (7 days)
        const SESSION_MAX_AGE = 7 * 24 * 60 * 60 * 1000
        const createdAt = localStorage.getItem('sessionCreatedAt')
        if (createdAt && Date.now() - Number(createdAt) > SESSION_MAX_AGE) {
          // Session expired — clear and redirect to login.
          // #841: device-id は browser 固有の安定識別子なので session
          // expiry でも保持(同一端末の再訪を「新端末」化させない)。
          clearLocalStoragePreservingDeviceId()
          navigate('/login', { replace: true })
          return
        }

        await restoreSession()
        navigate('/home', { replace: true })
        return
      }

      // ?skip_onboarding=true → skip to login
      if (skipOnboarding) {
        navigate('/login', { replace: true })
        return
      }

      // Check if onboarding has been seen
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding')
      if (hasSeenOnboarding) {
        navigate('/login', { replace: true })
        return
      }

      // First-time user → onboarding
      navigate('/onboarding', { replace: true })
    }, 2000)

    return () => clearTimeout(timer)
  }, [navigate, searchParams, setBalance, restoreSession])

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-navy to-navy-light safe-top safe-bottom">
      {/* Sakura Petals */}
      {PETALS.map((petal) => (
        <div
          key={petal.id}
          className="pointer-events-none absolute rounded-full bg-sakura"
          style={{
            left: petal.left,
            width: petal.size,
            height: petal.size,
            opacity: petal.opacity,
            animation: `float-sakura ${petal.duration}s ${petal.delay}s linear infinite`,
          }}
        />
      ))}

      {/* Main Content */}
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6">
        {/* Logo */}
        <div className="animate-scale-in mb-4">
          <h1 className="font-display text-5xl font-bold tracking-tight">
            <span className="text-white">YOKOSO</span>{' '}
            <span className="text-yokoso-red">PAY</span>
          </h1>
        </div>

        {/* Tagline */}
        <p
          className="animate-fade-in font-display text-lg font-semibold text-gold-light"
          style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
        >
          {t('splash.welcome')}
        </p>
      </div>

      {/* Loading Spinner at Bottom */}
      <div className="relative z-10 pb-12">
        <div className="h-6 w-6 rounded-full border-2 border-white/30 border-t-white animate-spin" />
      </div>
    </div>
  )
}
